import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SearchableSelect — [F-TEXTSUBTYPE] kereshető legördülő (combobox) a
 * miniappokhoz (ország- és körzet-választó). A MobileSelect mintájára
 * click-outside-dal záródik, de keresőmezőt, billentyűzet-navigációt
 * (fel/le/Enter/Escape) és ARIA szerepeket is ad.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
export function SearchableSelect({ options, value, onChange, disabled = false, placeholder = '', searchPlaceholder = '', emptyText = '-', className = '', buttonStyle, selectedLabelOf, }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const rootRef = useRef(null);
    const searchRef = useRef(null);
    const listRef = useRef(null);
    const selected = options.find((o) => o.value === value) || null;
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (q === '') {
            return options;
        }
        return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
    }, [options, query]);
    useEffect(() => {
        if (!open) {
            return;
        }
        const onDocMouseDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [open]);
    useEffect(() => {
        if (open) {
            setQuery('');
            setActiveIndex(-1);
            // a kereső azonnal írható
            setTimeout(() => searchRef.current?.focus(), 0);
        }
    }, [open]);
    useEffect(() => {
        if (activeIndex >= 0 && listRef.current) {
            const li = listRef.current.children[activeIndex];
            // jsdom-ban (Jest) nincs scrollIntoView — feature detection kell
            if (li && typeof li.scrollIntoView === 'function') {
                li.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [activeIndex]);
    const select = (v) => {
        onChange(v);
        setOpen(false);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        }
        else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && filtered[activeIndex]) {
                select(filtered[activeIndex].value);
            }
            else if (filtered.length === 1) {
                select(filtered[0].value);
            }
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
        }
    };
    return (_jsxs("div", { ref: rootRef, className: `searchable-select ${className}`.trim(), style: { position: 'relative', display: 'inline-block', flex: '0 0 auto' }, children: [_jsxs("button", { type: "button", role: "combobox", "aria-expanded": open, "aria-haspopup": "listbox", disabled: disabled, onClick: () => setOpen((o) => !o), onKeyDown: handleKeyDown, style: {
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    background: '#fff',
                    padding: '6px 10px',
                    cursor: disabled ? 'default' : 'pointer',
                    maxWidth: 220,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    ...(buttonStyle || {}),
                }, children: [selected ? (selectedLabelOf ? selectedLabelOf(selected) : selected.label) : placeholder, _jsx("span", { "aria-hidden": "true", style: { marginLeft: 6 }, children: "\u25BE" })] }), open && (_jsxs("div", { style: {
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    minWidth: 220,
                    marginTop: 2,
                }, children: [_jsx("input", { ref: searchRef, type: "text", value: query, placeholder: searchPlaceholder, onChange: (e) => {
                            setQuery(e.target.value);
                            setActiveIndex(0);
                        }, onKeyDown: handleKeyDown, autoComplete: "off", spellCheck: false, style: {
                            width: '100%',
                            boxSizing: 'border-box',
                            border: 'none',
                            borderBottom: '1px solid #eee',
                            padding: '8px 10px',
                            outline: 'none',
                        } }), _jsxs("ul", { ref: listRef, role: "listbox", style: {
                            listStyle: 'none',
                            margin: 0,
                            padding: 0,
                            maxHeight: 240,
                            overflowY: 'auto',
                        }, children: [filtered.length === 0 && (_jsx("li", { style: { padding: '8px 10px', color: '#999' }, children: emptyText })), filtered.map((o, i) => (_jsx("li", { role: "option", "aria-selected": o.value === value, onMouseDown: (e) => {
                                    // mousedown: a click-outside handler előtt fusson
                                    e.preventDefault();
                                    select(o.value);
                                }, onMouseEnter: () => setActiveIndex(i), style: {
                                    padding: '7px 10px',
                                    cursor: 'pointer',
                                    background: i === activeIndex ? '#f0f4f8' : o.value === value ? '#e9f5ef' : '#fff',
                                }, children: o.label }, o.value)))] })] }))] }));
}
export default SearchableSelect;
